import * as React from "react";

import { Emptyable, Locales, getField, getLocalesFromIsoLocale, Undefinable } from "@simplyk/common";
import { useRouter } from "next/router";

import {
  TicketingFrontendPageOutput,
  TicketingFieldOutput,
  DonationFormFrontendPageOutput,
  DonationFormFieldOutput,
} from "@/common/types/trpc";

const useParsedLocale = (): Locales => {
  const { locale } = useRouter();
  return React.useMemo(() => getLocalesFromIsoLocale(locale as Locales), [locale]);
};

export const useDonationFormTranslationField = (donationForm: DonationFormFrontendPageOutput) => {
  const locale = useParsedLocale();
  const donationFormField = donationForm?.donationFormFields && getField(donationForm?.donationFormFields, locale);

  return { donationFormField };
};

export const useTicketingTranslationField = (ticketing: TicketingFrontendPageOutput) => {
  const locale = useParsedLocale();
  const ticketingField = ticketing?.ticketingFields && getField(ticketing.ticketingFields, locale);

  return { ticketingField };
};

const isDonationForm = (
  form: DonationFormFrontendPageOutput | TicketingFrontendPageOutput
): form is DonationFormFrontendPageOutput => {
  return (form as DonationFormFrontendPageOutput).donationFormFields !== undefined;
};

export const useTranslationField = (
  form: Undefinable<DonationFormFrontendPageOutput | TicketingFrontendPageOutput>
): {
  field: { locale: Locales; id: string; title: string; donationFormAmounts?: { id: string }[] | null } | undefined;
  fieldLength: number | undefined;
  fields: Emptyable<DonationFormFieldOutput[]> | TicketingFieldOutput[] | undefined;
  postTransactionUrl?: string | null;
  chequeDescription?: string | null;
} => {
  const locale = useParsedLocale();

  if (form && isDonationForm(form)) {
    const fields = form.donationFormFields;
    const field = getField(fields, locale);
    const fieldLength = fields?.length;
    const postTransactionUrl = field?.postDonationUrl;
    const chequeDescription = field?.chequeDescription;
    return { fieldLength, fields, field, postTransactionUrl, chequeDescription };
  }

  const fields = form?.ticketingFields;
  const field = getField(fields, locale);
  const fieldLength = fields?.length;
  const postTransactionUrl = field?.postTransactionUrl;
  const chequeDescription = field?.chequeDescription;
  return { fieldLength, fields, field, postTransactionUrl, chequeDescription };
};
